import React, { useContext, useEffect, useState } from 'react';
import AdminLayout from '@app/layout/AdminLayout.tsx';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { ApiContext } from '@api/api';
import { RingilHeading } from '@components/styles.tsx';
import { AdminWebAlertRequestInDTO, ListWebAlertResponseOutDTO } from '@api/ringil-api.v2.tsx';
import { DataTable } from 'primereact/datatable';
import useForm from '@hooks/useForm/useForm';
import * as yup from 'yup';
import { Field } from '@components/framework/formfield/Field.tsx';
import RingilInputText from '@components/framework/input/RingilInput/RingilInputText.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import { findValidationMessage } from '@utils/validation.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { Calendar } from 'primereact/calendar';
import AddonsSvg from '@app/icons/AddonsSvg.tsx';
import { useTranslation } from 'react-i18next';
import { Column } from 'primereact/column';
import SvgDelete from '@icons/delete-trash.svg?react';
import { apiUtcFromDayjs, dayjsFromApiUtc, dayjsFromDateTimeInTz } from '@utils/dateWithTz.tsx';
import dayjs from 'dayjs';

interface ComponentProps {
}

type AdminWebAlertForm = AdminWebAlertRequestInDTO & { id?: string };


const IndexPage: React.FC<ComponentProps> = () => {
  const { tr } = useTranslationLgs();
  const { i18n } = useTranslation();
  const [alerts, setAlerts] = useState<ListWebAlertResponseOutDTO[]>([]);
  const [loading, setLoading] = useState(false);

  const { adminListWebAlert, adminCreateWebAlert, adminDeleteWebAlert, adminEditWebAlert } = useContext(ApiContext);

  useEffect(() => {
    loadAlerts();
  }, []);

  const loadAlerts = () => {
    adminListWebAlert(
      (data) => {
        setAlerts(data || []);
      },
    );
  };

  const schema = yup.object().shape({
    name: yup.string().required(),
    text: yup.string().required(),
    showFrom: yup.string().required(),
    showTo: yup.string().required(),
  });

  const form = useForm<AdminWebAlertForm>(
    schema,
    { id: undefined, name: '', text: '', showFrom: '', showTo: '' },
    (d) => save(d),
    false,
    false,
  );


  const save = (alert: AdminWebAlertRequestInDTO) => {
    setLoading(true);
    if (form.values.id) {
      adminEditWebAlert(form.values.id, alert, () => {
        setLoading(false);
        loadAlerts();
        resetForm();
      });
    } else {
      adminCreateWebAlert(alert, () => {
        setLoading(false);
        loadAlerts();
        resetForm();
      });
    }
  };

  const resetForm = () => {
    form.setFieldValue('id', undefined);
    form.setFieldValue('name', '');
    form.setFieldValue('text', '');
    form.setFieldValue('showFrom', '');
    form.setFieldValue('showTo', '');
  };

  const handleDelete = (row: ListWebAlertResponseOutDTO) => {
    setLoading(true);
    adminDeleteWebAlert(row.id, () => {
      if (form.values.id === row.id) {
        form.setFieldValue('id', undefined);
      }
      setLoading(false);
      loadAlerts();
    });
  };

  const handleEdit = (row: ListWebAlertResponseOutDTO) => {
    form.setFieldValue('id', row.id);
    form.setFieldValue('name', row.name);
    form.setFieldValue('text', row.text);
    form.setFieldValue('showFrom', row.showFrom);
    form.setFieldValue('showTo', row.showTo);
  };

  return (
    <>
      <AdminLayout title={'Web Alert'}>
        <RingilHeading>Web Alert</RingilHeading>
        <div className={'r-legacy-fields flex flex-col gap-1 mt-8 w-[400px]'}>
          <COHeadline title={'Add new alert'} variant={'h2'} className={'mb-4'} />


          {form.values.id && (
            <Field
              label={'Id'}
            >
              <RingilInputText
                value={form.values.id}
                onChange={() =>{}}
                fullWidth
                disabled
              />
            </Field>
          )}
          <Field
            label={'Name'}
            required={!form.values.name}
            errorMessage={findValidationMessage(form.validationErrors, form.names.name, tr)}
          >
            <RingilInputText
              value={form.values.name}
              onChange={value =>
                form.setFieldValue('name', value)}
              fullWidth
            />
          </Field>
          <Field
            label={'Text'}
            required={!form.values.text}
            errorMessage={findValidationMessage(form.validationErrors, form.names.text, tr)}
          >
            <RingilInputText
              value={form.values.text}
              onChange={value =>
                form.setFieldValue('text', value)}
              fullWidth
            />
          </Field>

          <Field
            label={'Show from'}
            required={!form.values.showFrom}
            errorMessage={findValidationMessage(form.validationErrors, form.names.showFrom, tr)}
          >
            <Calendar
              locale={i18n.language}
              selectionMode={'single'}
              value={form.values.showFrom ? dayjsFromApiUtc(form.values.showFrom).toDate() : null}
              onChange={e => {
                const d = e.value && !Array.isArray(e.value) ? e.value : null;
                form.setFieldValue('showFrom', d ? apiUtcFromDayjs(dayjsFromDateTimeInTz(d, 'Europe/Prague') as dayjs.Dayjs) : null);
              }}
              showIcon
              icon={<AddonsSvg width={20} height={20} />}
              showButtonBar={true}
              showWeek={true}
              showTime
              hourFormat="24"
            />
          </Field>

          <Field
            label={'Show to'}
            required={!form.values.showTo}
            errorMessage={findValidationMessage(form.validationErrors, form.names.showTo, tr)}
          >
            <Calendar
              locale={i18n.language}
              selectionMode={'single'}
              value={form.values.showTo ? dayjsFromApiUtc(form.values.showTo).toDate() : null}
              onChange={e => {
                const d = e.value && !Array.isArray(e.value) ? e.value : null;
                form.setFieldValue('showTo', d ? apiUtcFromDayjs(dayjsFromDateTimeInTz(d, 'Europe/Prague') as dayjs.Dayjs) : null);
              }}
              showIcon
              icon={<AddonsSvg width={20} height={20} />}
              showButtonBar={true}
              showWeek={true}
              showTime
              hourFormat="24"
            />
          </Field>

          <div className={'w-[300px] mt-4'}>
            <RingilButton
              label={`Continue`}
              onClick={() => form.validateAndSend()}
              mode={'filled'}
              size={'big'}
              loading={loading}
            />
          </div>
        </div>

        <div className={'r-legacy-fields mt-4'}>
          <COHeadline title={'Active alerts'} variant={'h2'} />
          <DataTable
            value={alerts}
          >
            <Column field="id" header="ID" />
            <Column field="name" header="Name" />
            <Column field="text" header="Text" />
            <Column field="showFrom" header="Available from" sortable />
            <Column field="showTo" header="Available to" sortable />
            <Column
              body={row => (
                <div className="flex gap-2">
                  <RingilButton
                    label="Edit"
                    size="small"
                    mode="table"
                    onClick={() => handleEdit(row)}
                  />
                  <RingilButton
                    icon={<SvgDelete />}
                    size="small"
                    mode="table"
                    onClick={() => handleDelete(row)}
                  />
                </div>
              )}
            />
          </DataTable>
        </div>
      </AdminLayout>
    </>
  );
};

export default withAuthenticationRequired(IndexPage);

